import { z } from "zod";
import { useFunnel } from "@airhelp/funnel";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import { updateClaimDetails, usePreEligibilityDataSnapshot } from "../state/preEligibilityState";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";

const options = [true, false];

const defaultStepSchema = z.object({
  voluntarilySurrendered: z.boolean().nullable(),
});

const stepSchema = z.object({
  voluntarilySurrendered: z.boolean(),
});

export function VolunteerStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { claimDetails, itineraryFlights, flightDetails } = usePreEligibilityDataSnapshot();
  const { createClaimEnquiry } = useCreateClaimEnquiry();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      voluntarilySurrendered: claimDetails.voluntarilySurrendered,
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ voluntarilySurrendered }) {
      const updatedClaimDetails = updateClaimDetails({ voluntarilySurrendered });

      if (funnel.currentStep?.meta?.claimShouldBeSubmitted) {
        const rawClaimEnquiry = await createClaimEnquiry({
          itineraryFlights,
          claimDetails: updatedClaimDetails,
          experiment: flightDetails.isFutureFlight ? "future_flight" : undefined,
        });
        trackClaimProcess({
          funnelStep: funnel.currentStep?.name,
          isEligible: rawClaimEnquiry?.claim_valid,
        });
        trackEnquiryStepComplete();
      } else {
        trackClaimProcess({ funnelStep: funnel.currentStep?.name });
        trackEnquiryStepComplete();
        funnel.stepForward();
      }
    },
  });

  return (
    <FunnelStepContainer data-testid={"voluntarilySurrenderedStep"}>
      <FunnelStepSection title={t("steps.volunteer.text")}>
        <ControlledRadioGroup
          name={"voluntarilySurrendered"}
          control={control}
          options={options}
          renderOption={(option) => t(`steps.volunteer.${option}`)}
        />
      </FunnelStepSection>
      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
